import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import useDoubleRequestSubmit from '../../../../../hooks/formHooks/useDoubleRequestSubmit'
import { formatTargetingRequestsToBE } from './formatters'

import {
  lineItemCreatedDataSelector,
  lineItemCreateErrorSelector,
  lineItemWasCreatedSelector
} from '../../../../../modules/selectors/lineItems'
import {
  dv360BulkTargetingWasUpdatedSelector,
  updateDv360BulkTargetingErrorSelector
} from '../../../../../modules/selectors/dv360Constants'
import { selectedDV360AdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import { dv360RightUploadAdDataSelector } from '../../../../../modules/selectors/rights'
import { clearAudiences } from '../../../../../modules/actions/audiences'
import { clearCreateLineItem, createLineItem } from '../../../../../modules/actions/lineItems'
import { clearUpdateDv360BulkTargeting, updateDv360BulkTargeting } from '../../../../../modules/actions/dv360Constants'

import { DV_360_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { transformValuesToBE } from './fields'

export function useLineItemDv360Create() {
  const dispatch = useDispatch()

  const [successSubmit, setSuccessSubmit] = useState(false)

  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)
  const { campaign } = useSelector(dv360RightUploadAdDataSelector)

  const createLineItemHandler = useCallback(
    values => {
      const formattedData = transformValuesToBE(values, campaign)

      const lineItemData = {
        account: selectedAdAccountId,
        campaign_id: campaign.id,
        ...formattedData
      }

      dispatch(createLineItem(lineItemData, DV_360_PLATFORM))
    },
    [dispatch, campaign, selectedAdAccountId]
  )

  const setLineItemTargetingHandler = useCallback(
    (values, lineItemCreatedData) => {
      const { id: lineItemId } = lineItemCreatedData

      const requests = formatTargetingRequestsToBE(values)

      if (requests.length > 0) {
        const updateData = {
          account: selectedAdAccountId,
          line_item_id: lineItemId,
          requests
        }

        dispatch(updateDv360BulkTargeting(updateData))
      } else {
        setSuccessSubmit(true)
      }
    },
    [dispatch, selectedAdAccountId]
  )

  const clearSubmitHandler = useCallback(() => {
    // after form submit
    dispatch(clearCreateLineItem())
    dispatch(clearUpdateDv360BulkTargeting())
    dispatch(clearAudiences())
  }, [dispatch])

  const createLineItemRequest = useMemo(
    () => ({
      requestHandler: createLineItemHandler,
      errorSelector: lineItemCreateErrorSelector,
      successSelector: lineItemWasCreatedSelector,
      dataSelector: lineItemCreatedDataSelector
    }),
    [createLineItemHandler]
  )

  const setLineItemAudiencesRequest = useMemo(
    () => ({
      requestHandler: setLineItemTargetingHandler,
      errorSelector: updateDv360BulkTargetingErrorSelector,
      successSelector: dv360BulkTargetingWasUpdatedSelector,
      manualSuccess: !!successSubmit
    }),
    [successSubmit, setLineItemTargetingHandler]
  )

  const requests = useMemo(() => {
    return [createLineItemRequest, setLineItemAudiencesRequest]
  }, [createLineItemRequest, setLineItemAudiencesRequest])

  return useDoubleRequestSubmit({ requests, clearSubmitHandler, keepFirstRequestProgress: true })
}
